import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useDialogState } from "../context/DialogStateHooks";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PixelDialogContentWrapperProps {
  children: React.ReactNode;
  className?: string;
}

export function PixelDialogContentWrapper({ 
  children, 
  className = "w-[800px] max-w-[90vw]"
}: PixelDialogContentWrapperProps) {
  const { 
    internalOpen, 
    handleOpenChange, 
    isSubmitting, 
    forceKeepOpen,
    installationPhase
  } = useDialogState();
  
  const [stableChildren, setStableChildren] = useState<React.ReactNode>(children);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const shouldPreventClose = isSubmitting;
  
  useEffect(() => {
    if (shouldPreventClose && !internalOpen) {
      handleOpenChange(true);
    }
  }, [internalOpen, isSubmitting, forceKeepOpen, installationPhase, shouldPreventClose, handleOpenChange, isTransitioning]);

  useEffect(() => {
    if (internalOpen && !isTransitioning) {
      setStableChildren(children);
    }
  }, [children, internalOpen, isTransitioning]);

  useEffect(() => {
    if (internalOpen) {
      setStableChildren(children);
    } else {
      setIsTransitioning(true);
      
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [internalOpen, children]);

  return (
    <Dialog 
      open={internalOpen} 
      onOpenChange={(open) => {
        if (!open && shouldPreventClose) {
          return;
        }
        
        if (!open && internalOpen) {
          setIsTransitioning(true);
        }
        
        handleOpenChange(open);
      }}
      modal={true}
    >
      <DialogContent 
        className={className}
        showCloseButton={true}
        preventCloseKeys={shouldPreventClose}
        onEscapeKeyDown={(e) => {
          if (shouldPreventClose) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (shouldPreventClose) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (shouldPreventClose) {
            e.preventDefault();
          }
        }}
      >
        <ScrollArea className="h-auto max-h-[80vh] w-full overflow-y-auto pr-4">
          <div className="px-2 py-1 pr-4">
            {isTransitioning || internalOpen ? stableChildren : children}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
